import * as React from "react";
import Image from "next/image";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const paperContainerVariants = cva(
  "bg-surface-primary dark:bg-surface-inset border flex-col md:flex-row rounded-lg overflow-clip p-3 flex md:items-center justify-between shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
  {
    variants: {
      variant: {
        default: "border-border",
        danger: "border-[rgba(237,59,59,0.4)]",
      },
      interactive: {
        true: "cursor-pointer hover:bg-surface-hover transition-colors",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      interactive: false,
    },
  },
);

export type PaperVariant = NonNullable<
  VariantProps<typeof paperContainerVariants>["variant"]
>;

/* ── Paper.Container ──────────────────────────────────────────────── */

interface ContainerProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof paperContainerVariants> {
  children: React.ReactNode;
}

function Container({
  variant,
  interactive,
  children,
  className,
  ...props
}: ContainerProps) {
  return (
    <div
      className={cn(
        paperContainerVariants({ variant, interactive }),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/* ── Paper.Icon ───────────────────────────────────────────────────── */

type IconSource =
  | { src: string; icon?: never; alt?: string }
  | {
      icon: React.ComponentType<{
        width?: number;
        height?: number;
        className?: string;
      }>;
      src?: never;
      alt?: never;
    };

type IconProps = IconSource & {
  size?: number;
  className?: string;
};

function Icon({ size = 14, className, alt = "", ...rest }: IconProps) {
  if ("icon" in rest && rest.icon) {
    const IconComponent = rest.icon;
    return (
      <IconComponent
        width={size}
        height={size}
        className={cn("size-3.5 text-contrast", className)}
      />
    );
  }
  return (
    <Image
      src={rest.src!}
      alt={alt}
      width={size}
      height={size}
      className={cn("size-3.5 brightness-0 dark:brightness-100", className)}
    />
  );
}

/* ── Paper.Title ──────────────────────────────────────────────────── */

interface TitleProps {
  children: React.ReactNode;
  className?: string;
}

function Title({ children, className }: TitleProps) {
  return (
    <span
      className={cn("text-sm font-medium leading-4 text-heading", className)}
    >
      {children}
    </span>
  );
}

/* ── Paper.Description ────────────────────────────────────────────── */

interface DescriptionProps {
  children: React.ReactNode;
  className?: string;
}

function Description({ children, className }: DescriptionProps) {
  return (
    <span
      className={cn(
        "text-[13px] text-contrast/60 dark:text-contrast/70",
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ── Paper.Body ───────────────────────────────────────────────────── */

interface BodyProps {
  children: React.ReactNode;
  className?: string;
}

function Body({ children, className }: BodyProps) {
  return (
    <div className={cn("flex-1 flex flex-col gap-1.5", className)}>
      {children}
    </div>
  );
}

/* ── Paper.TitleRow ───────────────────────────────────────────────── */

interface TitleRowProps {
  children: React.ReactNode;
  className?: string;
}

function TitleRow({ children, className }: TitleRowProps) {
  return (
    <div className={cn("flex items-center gap-1.5", className)}>{children}</div>
  );
}

/* ── Namespace export ─────────────────────────────────────────────── */

export const Paper = {
  Container,
  Icon,
  Title,
  Description,
  Body,
  TitleRow,
};
